// BUILD-046 section C (Owner direction of 2026-10-21): the microphone of the Update note. It is the same browser speech recognition HQ chat already uses
// (dictationModel.ts): no provider, no key, nothing sent to this application. It is drawn ONLY where the browser has the capability; otherwise nothing is shown
// (no disabled or pretend microphone). The spoken text is added after what was already typed; the person can edit it afterwards and nothing is saved until Save.
import { useEffect, useRef, useState } from "react";
import { dictationSupported, mergeTranscript, recognitionOf } from "./dictationModel";

export interface DictationButtonProps {
  /** The text in the field right now. */
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
}

const BUTTON = "inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:opacity-50";

export function DictationButton({ value, onChange, disabled = false }: DictationButtonProps) {
  const [listening, setListening] = useState(false);
  const [problem, setProblem] = useState("");
  const recognition = useRef<{ stop?: () => void } | null>(null);
  const base = useRef("");
  const latest = useRef(onChange);
  latest.current = onChange;
  useEffect(() => () => { recognition.current?.stop?.(); }, []);
  if (!dictationSupported()) return null;

  function toggle(): void {
    if (listening) {
      recognition.current?.stop?.();
      setListening(false);
      return;
    }
    const Recognition = recognitionOf();
    if (Recognition === null) return;
    const instance = new Recognition() as { continuous: boolean; interimResults: boolean; lang: string; onresult: (event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void; onend: () => void; onerror: (event: { error?: string }) => void; start: () => void; stop: () => void };
    instance.continuous = true;
    instance.interimResults = true;
    instance.lang = "en-US";
    base.current = value;
    instance.onresult = (event) => {
      let spoken = "";
      for (let i = 0; i < event.results.length; i++) spoken += event.results[i][0].transcript;
      latest.current(mergeTranscript(base.current, spoken));
    };
    instance.onend = () => setListening(false);
    instance.onerror = (event) => {
      setListening(false);
      setProblem(event.error === "not-allowed" ? "The microphone is blocked in this browser." : "Dictation stopped.");
    };
    recognition.current = instance;
    setProblem("");
    instance.start();
    setListening(true);
  }

  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" onClick={toggle} disabled={disabled} aria-pressed={listening} aria-label={listening ? "Stop dictating the note" : "Dictate the note"} title={listening ? "Stop dictating" : "Dictate the note with the microphone"} data-testid="dictate-note" className={BUTTON}>
        <svg aria-hidden="true" viewBox="0 0 20 20" className={"h-3.5 w-3.5 " + (listening ? "text-red-600" : "text-gray-600")} fill="currentColor"><path d="M10 12a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v4a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.92V18h2a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h2v-2.08A7 7 0 0 1 3 9a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z" /></svg>
        {listening ? "Stop" : "Dictate"}
      </button>
      {listening && <span role="status" className="text-xs text-red-700">Listening...</span>}
      {problem !== "" && <span role="status" className="text-xs text-red-700">{problem}</span>}
    </span>
  );
}
